/**
 * Unit Types API
 *
 * All unit type-related data access functions.
 * Uses RLS policies for authorization.
 */

import { supabase, normalizeError } from '@/lib/supabaseClient'

/**
 * Fetch all unit types for a project
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchUnitTypes(projectId) {
  try {
    const { data, error } = await supabase
      .from('unit_types')
      .select('*')
      .eq('project_id', projectId)
      .order('display_order', { ascending: true })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch single unit type by ID
 *
 * @param {string} unitTypeId - Unit Type UUID
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function fetchUnitTypeById(unitTypeId) {
  try {
    const { data, error } = await supabase
      .from('unit_types')
      .select('*')
      .eq('id', unitTypeId)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Create a new unit type
 *
 * @param {Object} unitTypeData - Unit type data
 * @param {string} unitTypeData.project_id - Project UUID
 * @param {string} unitTypeData.label - Label (A, B, C, etc.)
 * @param {number} unitTypeData.bedrooms - Number of bedrooms
 * @param {number} unitTypeData.bathrooms - Number of bathrooms
 * @param {number} unitTypeData.garages - Number of garages
 * @param {number} unitTypeData.car_parks - Number of car parks
 * @param {number} unitTypeData.internal_size_sqm - Internal size in sqm
 * @param {number} unitTypeData.default_sale_price - Default sale price
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function createUnitType(unitTypeData) {
  try {
    const { data, error } = await supabase
      .from('unit_types')
      .insert(unitTypeData)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Update existing unit type
 *
 * @param {string} unitTypeId - Unit Type UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function updateUnitType(unitTypeId, updates) {
  try {
    const { data, error } = await supabase
      .from('unit_types')
      .update(updates)
      .eq('id', unitTypeId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Delete unit type
 *
 * @param {string} unitTypeId - Unit Type UUID
 * @returns {Promise<{error: Object|null}>}
 */
export async function deleteUnitType(unitTypeId) {
  try {
    const { error } = await supabase
      .from('unit_types')
      .delete()
      .eq('id', unitTypeId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Get next available unit type label for a project
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: string|null, error: Object|null}>}
 */
export async function getNextUnitTypeLabel(projectId) {
  try {
    const { data, error } = await supabase.rpc('get_next_unit_type_label', {
      p_project_id: projectId,
    })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Reorder unit types
 *
 * @param {Array<{id: string, display_order: number}>} reorderedTypes - Array of unit types with new display orders
 * @returns {Promise<{error: Object|null}>}
 */
export async function reorderUnitTypes(reorderedTypes) {
  try {
    const updates = reorderedTypes.map(({ id, display_order }) =>
      supabase
        .from('unit_types')
        .update({ display_order })
        .eq('id', id)
    )

    const results = await Promise.all(updates)

    const firstError = results.find(r => r.error)
    if (firstError?.error) throw firstError.error

    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Get count of units using a specific unit type
 *
 * @param {string} unitTypeId - Unit Type UUID
 * @returns {Promise<{data: number, error: Object|null}>}
 */
export async function getUnitTypeUsageCount(unitTypeId) {
  try {
    const { count, error } = await supabase
      .from('units')
      .select('*', { count: 'exact', head: true })
      .eq('unit_type_id', unitTypeId)

    if (error) throw error
    return { data: count || 0, error: null }
  } catch (error) {
    return { data: 0, error: normalizeError(error) }
  }
}
